/**
 * Shared child-ordering contract for the files backends.
 *
 * Once a folder is paginated, the client can no longer sort its children
 * itself — it only ever holds *some* pages, so a row from a later page could
 * sort above a row already shown. The order therefore has to be a stable,
 * deterministic TOTAL order that both the server (KB) and the in-memory
 * adapter (VFS) agree on, so paginated pages concatenate correctly.
 *
 * Two modes, matching the two list views:
 *
 *  - `compareChildren` — DIRECT-CHILDREN (tree view, `recursive=false`):
 *    folders first, then files, each group ascending by name (case-insensitive),
 *    full `path` as the tiebreaker so the order is strict (two files named
 *    `report.pdf` under different folders never compare equal).
 *
 *  - `compareFlat` — FLAT LIST (list view, `recursive=true`): files only, the
 *    same name-asc + path tiebreak. Folders are omitted from the flat list (you
 *    can only open/mention files; folders come from the tree view).
 *
 * The KB server MUST return pages already in these orders (see the backend
 * asks in the plan); VFS holds the whole list in memory and sorts with the
 * exact same comparators so the two backends are indistinguishable to the host.
 */

import type { FolderChild } from './types';

/** Case-insensitive name compare, `path` as the strict tiebreaker. */
function byNameThenPath(a: FolderChild, b: FolderChild): number {
  const n = a.name.localeCompare(b.name, undefined, { sensitivity: 'accent' });
  if (n !== 0) return n;
  return a.path < b.path ? -1 : a.path > b.path ? 1 : 0;
}

/** Direct-children order: folders first, then files, each name-asc + path. */
export function compareChildren(a: FolderChild, b: FolderChild): number {
  if (a.kind !== b.kind) return a.kind === 'folder' ? -1 : 1;
  return byNameThenPath(a, b);
}

/** Flat-list order: files only (caller filters folders out), name-asc + path. */
export function compareFlat(a: FolderChild, b: FolderChild): number {
  return byNameThenPath(a, b);
}

/** Sort a fully-in-memory child set for one folder (VFS). Returns a new array. */
export function sortDirectChildren(children: FolderChild[]): FolderChild[] {
  return children.slice().sort(compareChildren);
}

/** Sort + filter a fully-in-memory descendant set into the flat-list order (VFS). */
export function sortFlatFiles(children: FolderChild[]): FolderChild[] {
  return children.filter((c) => c.kind === 'file').sort(compareFlat);
}
